import { Route, Routes, useNavigate, useResolvedPath } from 'react-router-dom';
import { IAppointment, KeyStatus } from '../../API/GoMoto';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentStateUnsafe } from '../../context/Appointment/appointment';
import { useFullReset, useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { Page } from '../Layout/Page/Page';
import { ResendQRCodeForm } from '../SaveSession/ResendQRCode';
import { ScanQRCode } from '../SaveSession/ScanQRCode/ScanQRCode/ScanQRCode';
import { KeyDropPickUp } from './KeyDropPickup/KeyDropPickUp';
import { Payment } from './Payment/Payment';
import { PathName, VehiclePickupRoutePaths } from '../../helpers/Routes';
import { GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';
import { memo, useRef } from 'react';
import { GMDoubleCheckLocker } from '../../controls/DoubleCheckLocker';
import { AppointmentLoanerOrRentalOption } from '../../API/Enums';

export const VehiclePickUpRoutes: GoMotoComponent = memo(() => {
    const navigate = useNavigate();
    const { pathname } = useResolvedPath('.');
    const { kioskType } = useKioskState();
    const reset = useFullReset();
    const Localization = useLocalization();
    const appointmentDispatch = useAppointmentDispatch();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();

    const appointmentRef = useRef<IAppointment | null>(null);

    return (
        <Page>
            <Routes>
                <Route
                    path={''}
                    element={
                        <ScanQRCode
                            validateAppointmentKey
                            onProceed={(_, updatedAppointment) => {
                                appointmentRef.current = updatedAppointment;

                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.Update,
                                    payload: updatedAppointment,
                                });

                                if (updatedAppointment?.paymentStatus === 'not paid') {
                                    navigate(PathName(pathname, VehiclePickupRoutePaths.Payment));
                                }
                                else {
                                    navigate(PathName(pathname, VehiclePickupRoutePaths.KeydropPickup));
                                }
                            }}
                            onResendClick={() => {
                                navigate(PathName(pathname, VehiclePickupRoutePaths.Resend));
                            }}
                            primaryHeading={Localization.KeyPickup}
                            secondaryHeading={Localization.VehiclePickUpSecondaryHeading}
                            headingIconType={'Key'}
                        />
                    }
                />
                <Route
                    path={PathName(VehiclePickupRoutePaths.Resend, '*')}
                    element={
                        <ResendQRCodeForm
                            onProceed={() => {
                                navigate(pathname);
                            }}
                        />
                    }
                />
                <Route
                    path={PathName(VehiclePickupRoutePaths.Payment, '*')}
                    element={
                        <Payment
                            onProceed={(updated: IAppointment) => {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.Update,
                                    payload: updated,
                                });

                                navigate(PathName(pathname, VehiclePickupRoutePaths.KeydropPickup), { replace: true });
                            }}
                            onSkip={() => navigate(PathName(pathname, VehiclePickupRoutePaths.KeydropPickup), { replace: true })}
                        />
                    }
                />
                {appointment && (
                    <Route
                        path={PathName(VehiclePickupRoutePaths.DoubleCheck, '*')}
                        element={
                            <GMDoubleCheckLocker
                                lockerOperationType={'pick-up'}
                                onContinue={reset}
                                onReopenLocker={async () => {
                                    const getKeyStatus: GoMotoFunction<void, KeyStatus> = () => {
                                        switch (appointment.returningLoanerOrRental) {
                                            case AppointmentLoanerOrRentalOption.Loaner:
                                            case AppointmentLoanerOrRentalOption.Rental:
                                                return 'reopen requested exchange';
                                            default:
                                                return 'reopen requested pickup';
                                        }
                                    };

                                    appointmentDispatch({
                                        type: AppointmentDispatchTypes.Update,
                                        payload: {
                                            ...appointment,
                                            ...(appointmentRef.current !== null ? { ...appointmentRef.current } : {}),
                                            keyStatus: getKeyStatus(),
                                        },
                                    });

                                    navigate(PathName(pathname, VehiclePickupRoutePaths.KeydropPickup));
                                }}
                            />
                        }
                    />
                )}
                {appointment && (
                    <Route
                        path={PathName(VehiclePickupRoutePaths.KeydropPickup, '*')}
                        element={
                            <KeyDropPickUp
                                onProceed={() => {
                                    if (kioskType === 'express') {
                                        navigate(PathName(pathname, VehiclePickupRoutePaths.DoubleCheck));
                                    }
                                    else {
                                        reset();
                                    }
                                }}
                                onSkip={reset}
                            />
                        }
                    />
                )}
            </Routes>
        </Page>
    );
});
